import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Paper, CircularProgress, Tooltip, Chip, InputAdornment, Fade, alpha
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchSiteSettingsAdmin,
    updateSiteSettings,
    clearSuccess,
    clearError
} from '../../../Helpers/siteSettingsSlice';
import toast from 'react-hot-toast';
import {
    LinkOutlined,
    LanguageOutlined,
    HomeOutlined,
    ContactMailOutlined,
    EditOutlined,
    TuneOutlined,
} from '@mui/icons-material';

const FIELD_CONFIG = {
    header: {
        title: 'Header Section',
        description: 'Statistics displayed in the hero section and features area of the home page.',
        icon: <LanguageOutlined />,
        color: '#3B82F6',
        fields: [
            { key: 'companies_trust_us_count', label: 'Companies Trust Us (Count)', hint: 'Numeric value shown in the hero counter (e.g., 30)', numberOnly: true },
            { key: 'trusted_by_text', label: 'Trusted By Text', hint: 'Auto-updated when Companies Trust Us count changes', linked: true, disabled: true },
            { key: 'issues_resolved', label: 'Issues Resolved', hint: 'Value shown in hero counter with "k+" suffix (e.g., 12.5)' },
            { key: 'customer_satisfaction', label: 'Customer Satisfaction', hint: 'Percentage shown in hero counter with "%" suffix (e.g., 99.5)' },
            { key: 'total_savings', label: 'Total Savings', hint: 'Percentage shown in hero counter with "%+" suffix (e.g., 80)' },
            { key: 'quality_issues_resolved', label: 'Quality Issues Resolved', hint: 'Displayed in the features section banner (e.g., 12.5k+)' },
        ]
    },
    home: {
        title: 'Home Page',
        description: 'Key metrics and performance values displayed across the home page sections.',
        icon: <HomeOutlined />,
        color: '#8B5CF6',
        fields: [
            { key: 'time_saved', label: 'Time Saved', hint: 'Percentage shown with "%" suffix in hero stats (e.g., 95)' },
            { key: 'cost_reduction', label: 'Cost Reduction', hint: 'Percentage shown with "%" suffix in hero stats (e.g., 87)' },
            { key: 'monthly_savings', label: 'Monthly Savings (Value)', hint: 'Value displayed as "₹ {value}" (e.g., 35K)' },
            { key: 'root_cause_analysis', label: 'Root Cause Analysis', hint: 'Time displayed in the hero and speed demo cards (e.g., 5 min)' },
            { key: 'resolution_rate', label: 'Resolution Rate', hint: 'Percentage shown with "%" suffix (e.g., 97)' },
            { key: 'ai_analysis_data_points', label: 'AI Analysis Active (Value)', hint: 'Number of data points shown in AI card (e.g., 247)' },
            { key: 'report_generation', label: 'Report Generation', hint: 'Time displayed in speed demo section (e.g., 30sec)' },
        ]
    },
    footer: {
        title: 'Footer Section',
        description: 'Contact information, company details, and demo section contact info.',
        icon: <ContactMailOutlined />,
        color: '#10B981',
        fields: [
            { key: 'contact_email', label: 'Contact Us – Mail Address', hint: 'Email in footer Contact Us section' },
            { key: 'contact_phone', label: 'Contact Us – Mobile Number', hint: 'Phone number in footer Contact Us section' },
            { key: 'company_name', label: 'Company Name', hint: 'Company name in footer Company Info section' },
            { key: 'company_location', label: 'Company Location', hint: 'Location in footer Company Info section' },
            { key: 'company_uen', label: 'Company UEN', hint: 'UEN number in footer Company Info section' },
            { key: 'seller_name', label: 'Seller Name', hint: 'Seller name in footer Company Info section' },
            { key: 'seller_gstin', label: 'Seller GSTIN', hint: 'GSTIN number in footer Company Info section' },
            { key: 'demo_contact_email', label: 'Contact Us Directly – Mail Address', hint: 'Email in the demo section contact box' },
            { key: 'demo_contact_phone', label: 'Contact Us Directly – Mobile Number', hint: 'Phone number in the demo section contact box' },
        ]
    }
};

export default function SiteSettings() {
    const dispatch = useDispatch();
    const { settingsData, isLoading, successMessage, error } = useSelector((state) => state.siteSettings);
    const accessToken = localStorage.getItem('NCProAccessToken');

    const [formValues, setFormValues] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (accessToken) {
            dispatch(fetchSiteSettingsAdmin(accessToken));
        }
    }, [accessToken, dispatch]);

    useEffect(() => {
        if (settingsData && settingsData.length > 0) {
            const values = {};
            settingsData.forEach(setting => {
                values[setting.setting_key] = setting.setting_value;
            });
            setFormValues(values);
            setHasChanges(false);
        }
    }, [settingsData]);

    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
            dispatch(clearSuccess());
            setSaving(false);
            setHasChanges(false);
        }
    }, [successMessage, dispatch]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
            setSaving(false);
        }
    }, [error, dispatch]);

    const handleChange = (key) => (e) => {
        const newValues = { ...formValues, [key]: e.target.value };

        // Handle linked fields
        if (key === 'companies_trust_us_count') {
            newValues['trusted_by_text'] = `Trusted by ${e.target.value}+ Manufacturing Companies Worldwide`;
        }

        setFormValues(newValues);
        setHasChanges(true);
    };

    const handleSave = () => {
        // Validate no empty or whitespace-only values
        const emptyFields = [];
        Object.entries(FIELD_CONFIG).forEach(([, group]) => {
            group.fields.forEach(field => {
                const value = formValues[field.key];
                if (!value || !value.toString().trim()) {
                    emptyFields.push(field.label);
                }
            });
        });

        if (emptyFields.length > 0) {
            toast.error(`Please fill in all fields. Empty: ${emptyFields[0]}${emptyFields.length > 1 ? ` and ${emptyFields.length - 1} more` : ''}`);
            return;
        }

        setSaving(true);
        const changedSettings = [];

        settingsData.forEach(setting => {
            if (formValues[setting.setting_key] !== setting.setting_value) {
                changedSettings.push({
                    setting_key: setting.setting_key,
                    setting_value: formValues[setting.setting_key].toString().trim()
                });
            }
        });

        if (changedSettings.length === 0) {
            toast.error("No changes to save");
            setSaving(false);
            return;
        }

        dispatch(updateSiteSettings(changedSettings, accessToken));
    };

    const handleReset = () => {
        const values = {};
        settingsData.forEach(setting => {
            values[setting.setting_key] = setting.setting_value;
        });
        setFormValues(values);
        setHasChanges(false);
    };

    const changedCount = settingsData
        ? settingsData.filter(s => formValues[s.setting_key] !== s.setting_value).length
        : 0;

    if (isLoading && settingsData.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress sx={{ color: '#0E2966' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 13, mb: 12 }}>
            {/* Page Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 4,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #0E2966, #1a3a8a)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 14px rgba(14, 41, 102, 0.3)',
                    }}>
                        <TuneOutlined sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: 'Lato, sans-serif',
                                fontWeight: 800,
                                color: '#0E2966',
                                fontSize: '22px',
                                letterSpacing: '-0.3px',
                            }}
                        >
                            Site Settings
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'Lato, sans-serif',
                                color: '#94A3B8',
                                mt: 0.3,
                                fontSize: '13px',
                            }}
                        >
                            Manage editable content displayed on the home page
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Section Cards */}
            {Object.entries(FIELD_CONFIG).map(([groupKey, group], index) => (
                <Fade in timeout={400 + index * 200} key={groupKey}>
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            border: '1px solid',
                            borderColor: alpha(group.color, 0.15),
                            borderRadius: '16px',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                            '&:hover': {
                                boxShadow: `0 4px 20px ${alpha(group.color, 0.1)}`,
                                borderColor: alpha(group.color, 0.3),
                            },
                        }}
                    >
                        {/* Section Header */}
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                background: `linear-gradient(135deg, ${alpha(group.color, 0.04)} 0%, ${alpha(group.color, 0.08)} 100%)`,
                                borderBottom: '1px solid',
                                borderColor: alpha(group.color, 0.1),
                            }}
                        >
                            <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                backgroundColor: alpha(group.color, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: group.color,
                                '& .MuiSvgIcon-root': { fontSize: 20 },
                            }}>
                                {group.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Lato, sans-serif',
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        color: '#1E293B',
                                    }}
                                >
                                    {group.title}<Box component="span" sx={{ color: '#EF4444', ml: 0.5 }}>*</Box>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'Lato, sans-serif',
                                        color: '#94A3B8',
                                        fontSize: '12px',
                                    }}
                                >
                                    {group.description}
                                </Typography>
                            </Box>
                            <Chip
                                label={`${group.fields.length} fields`}
                                size="small"
                                sx={{
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    backgroundColor: alpha(group.color, 0.08),
                                    color: group.color,
                                    border: `1px solid ${alpha(group.color, 0.15)}`,
                                    height: 26,
                                }}
                            />
                        </Box>

                        {/* Fields Grid */}
                        <Box sx={{ px: 3, py: 2.5 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                                {group.fields.map((field) => {
                                    const isChanged = settingsData &&
                                        settingsData.find(s => s.setting_key === field.key)?.setting_value !== formValues[field.key];

                                    return (
                                        <Box key={field.key} sx={{ width: 'calc(50% - 10px)', minWidth: '280px' }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Lato, sans-serif',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                    mb: 0.8,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {field.label}
                                                {field.linked && (
                                                    <Tooltip title="Auto-updated when 'Companies Trust Us (Count)' changes" arrow placement="top">
                                                        <LinkOutlined sx={{ fontSize: 15, color: '#94A3B8', cursor: 'help' }} />
                                                    </Tooltip>
                                                )}
                                                {isChanged && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#F59E0B',
                                                            display: 'inline-block',
                                                            ml: 0.5,
                                                        }}
                                                    />
                                                )}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={formValues[field.key] || ''}
                                                onChange={(e) => {
                                                    if (field.numberOnly) {
                                                        const value = e.target.value.replace(/[^0-9.]/g, '');
                                                        handleChange(field.key)({ target: { value } });
                                                    } else {
                                                        handleChange(field.key)(e);
                                                    }
                                                }}
                                                disabled={field.disabled}
                                                placeholder={field.hint}
                                                {...(field.numberOnly && {
                                                    inputMode: 'numeric',
                                                    onPaste: (e) => {
                                                        const pasted = e.clipboardData.getData('text');
                                                        if (!/^\d*\.?\d*$/.test(pasted)) {
                                                            e.preventDefault();
                                                        }
                                                    },
                                                })}
                                                slotProps={{
                                                    input: {
                                                        ...(field.disabled && {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LinkOutlined sx={{ fontSize: 16, color: '#CBD5E1' }} />
                                                                </InputAdornment>
                                                            ),
                                                        }),
                                                        ...(!field.disabled && {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <EditOutlined sx={{ fontSize: 14, color: '#CBD5E1' }} />
                                                                </InputAdornment>
                                                            ),
                                                        }),
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontFamily: 'Lato, sans-serif',
                                                        fontSize: '13px',
                                                        borderRadius: '10px',
                                                        backgroundColor: field.disabled ? '#F8FAFC' : '#fff',
                                                        transition: 'all 0.2s ease',
                                                        '& fieldset': {
                                                            borderColor: isChanged ? '#F59E0B' : '#E2E8F0',
                                                            borderWidth: isChanged ? '2px' : '1px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: isChanged ? '#F59E0B' : group.color,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: group.color,
                                                            borderWidth: '2px',
                                                        },
                                                    },
                                                    '& .Mui-disabled': {
                                                        WebkitTextFillColor: '#64748B',
                                                    },
                                                }}
                                            />
                                            {field.hint && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontFamily: 'Lato, sans-serif',
                                                        color: '#78849E',
                                                        mt: 0.5,
                                                        display: 'block',
                                                        fontSize: '11px',
                                                    }}
                                                >
                                                    {field.hint}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Paper>
                </Fade>
            ))}

            {/* Fixed Bottom Action Bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 240,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    borderTop: '1px solid #E2E8F0',
                    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.06)',
                    px: 3,
                    py: 1.8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {hasChanges && (
                        <Fade in>
                            <Chip
                                label={`${changedCount} unsaved change${changedCount !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    backgroundColor: '#FEF3C7',
                                    color: '#D97706',
                                    border: '1px solid #FDE68A',
                                    height: 28,
                                    '& .MuiChip-label': { px: 1.5 },
                                }}
                            />
                        </Fade>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                        sx={{
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '13px',
                            fontWeight: 600,
                            border: '1px solid #E2E8F0',
                            textTransform: 'none',
                            borderRadius: '10px',
                            color: '#64748B',
                            px: 2.5,
                            py: 0.8,
                            opacity: (!hasChanges || saving) ? 0.4 : 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                border: '1px solid #CBD5E1',
                                backgroundColor: '#F8FAFC',
                            },
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        sx={{
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '13px',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #0E2966 0%, #1E40AF 50%, #3B82F6 100%)',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '10px',
                            px: 3,
                            py: 0.8,
                            opacity: (!hasChanges || saving) ? 0.4 : 1,
                            boxShadow: hasChanges ? '0 4px 14px rgba(14, 41, 102, 0.3)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0a1f4e 0%, #1a3699 50%, #2563EB 100%)',
                                boxShadow: '0 6px 20px rgba(14, 41, 102, 0.4)',
                            },
                            '&:disabled': {
                                color: 'white',
                            },
                        }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
